import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Layers, Plus, Edit2, Trash2, Save, Shield, Check, X,
  Eye, FilePlus, Pencil, Trash, Download, Upload, CheckCircle
} from 'lucide-react';
import { useRBAC } from '@/contexts/RBACContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = [
  { code: 'view', label: 'View', icon: Eye, color: 'text-blue-500' },
  { code: 'create', label: 'Create', icon: FilePlus, color: 'text-green-500' },
  { code: 'edit', label: 'Edit', icon: Pencil, color: 'text-amber-500' },
  { code: 'delete', label: 'Delete', icon: Trash, color: 'text-red-500' },
  { code: 'export', label: 'Export', icon: Download, color: 'text-purple-500' },
  { code: 'import', label: 'Import', icon: Upload, color: 'text-indigo-500' },
  { code: 'approve', label: 'Approve', icon: CheckCircle, color: 'text-emerald-500' },
];

const PermissionGroups = () => {
  // Backend clears both permission + navigation caches on any RBAC write,
  // but the current admin's own sidebar is held in React state by
  // RBACContext and won't re-fetch until page mount. Call refreshNavigation()
  // after every save so the admin's OWN sidebar picks up the change without
  // a logout. OTHER users still need to log out+in in their own browsers —
  // the backend can't push nav to them.
  const { refreshNavigation } = useRBAC();
  const [groups, setGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Group dialog state
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    group_code: '',
    group_name: '',
    description: '',
    status: 'active'
  });
  
  // Permission assignment state
  const [groupPermissions, setGroupPermissions] = useState({});

  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [groupsRes, permsRes] = await Promise.all([
        axios.get(`${API}/api/admin/permission-groups?token=${token}&include_inactive=true`),
        axios.get(`${API}/api/admin/permissions/catalog?token=${token}`)
      ]);
      
      if (groupsRes.data.success) {
        setGroups(groupsRes.data.groups);
        if (groupsRes.data.groups.length > 0 && !selectedGroup) {
          selectGroup(groupsRes.data.groups[0]);
        }
      }
      
      if (permsRes.data.success) {
        setPermissions(permsRes.data.permissions);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectGroup = (group) => {
    setSelectedGroup(group);
    // Build permission map from group items
    const permMap = {};
    (group.items || []).forEach(item => {
      permMap[item.permission_id] = item.allowed_actions || [];
    });
    setGroupPermissions(permMap);
  };

  // Group CRUD
  const openGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        group_code: group.group_code,
        group_name: group.group_name,
        description: group.description || '',
        status: group.status
      });
    } else {
      setEditingGroup(null);
      setGroupForm({
        group_code: '',
        group_name: '',
        description: '',
        status: 'active'
      });
    }
    setShowGroupDialog(true);
  };

  const saveGroup = async () => {
    try {
      if (editingGroup) {
        await axios.put(`${API}/api/admin/permission-groups/${editingGroup.id}?token=${token}`, groupForm);
        toast.success('Group updated');
      } else {
        await axios.post(`${API}/api/admin/permission-groups?token=${token}`, groupForm);
        toast.success('Group created');
      }
      setShowGroupDialog(false);
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save group');
    }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/api/admin/permission-groups/${groupId}?token=${token}`);
      toast.success('Permission group deleted');
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
      }
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  // Permission assignment
  const togglePermissionAction = (permissionId, action) => {
    setGroupPermissions(prev => {
      const current = prev[permissionId] || [];
      const updated = current.includes(action)
        ? current.filter(a => a !== action)
        : [...current, action];
      
      if (updated.length === 0) {
        const { [permissionId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [permissionId]: updated };
    });
  };

  const toggleAllActions = (permissionId, actions) => {
    setGroupPermissions(prev => {
      const current = prev[permissionId] || [];
      const allSelected = actions.every(a => current.includes(a));
      
      if (allSelected) {
        const { [permissionId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [permissionId]: actions };
    });
  };

  const saveGroupPermissions = async () => {
    if (!selectedGroup) return;
    
    try {
      const permissionsToSave = Object.entries(groupPermissions).map(([permId, actions]) => ({
        permission_id: permId,
        allowed_actions: actions
      }));
      
      await axios.put(
        `${API}/api/admin/permission-groups/${selectedGroup.id}/permissions?token=${token}`,
        { permissions: permissionsToSave }
      );

      toast.success(
        `Permissions saved for "${selectedGroup.group_name || selectedGroup.group_code}". ` +
        `Users with roles that include this group must log out and log back in to see the change.`,
      );
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error('Failed to save permissions');
    }
  };

  // Group permissions by menu
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const menuCode = perm.permission_code.split('.')[0];
    if (!acc[menuCode]) acc[menuCode] = [];
    acc[menuCode].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="permission-groups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Permission Groups</h1>
          <p className="text-slate-600">Create reusable bundles of permissions</p>
        </div>
        <Button onClick={() => openGroupDialog()} data-testid="add-group-btn">
          <Plus size={16} className="mr-2" />
          Add Group
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Groups List */}
        <div className="col-span-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Layers size={16} />
              Permission Groups
            </h3>
            <div className="space-y-2">
              {groups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => selectGroup(group)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedGroup?.id === group.id
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{group.group_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{group.group_code}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs">
                        {group.items?.length || 0} perms
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGroupDialog(group);
                        }}
                        title="Edit"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteGroup(group.id, group.group_name);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                  {group.description && (
                    <p className="text-xs text-slate-500 mt-1">{group.description}</p>
                  )}
                </div>
              ))}
              
              {groups.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Layers size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No permission groups yet</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Permissions Assignment */}
        <div className="col-span-8">
          <Card className="p-4">
            {selectedGroup ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      <Shield size={16} />
                      {selectedGroup.group_name} Permissions
                    </h3>
                    <p className="text-sm text-slate-500">
                      Select permissions and actions for this group
                    </p>
                  </div>
                  <Button onClick={saveGroupPermissions} data-testid="save-permissions-btn">
                    <Save size={14} className="mr-2" />
                    Save Permissions
                  </Button>
                </div>

                <div className="space-y-6 max-h-[600px] overflow-y-auto">
                  {Object.entries(groupedPermissions).map(([menuCode, menuPerms]) => (
                    <div key={menuCode} className="border rounded-lg p-4">
                      <h4 className="font-medium text-sm text-slate-700 mb-3 uppercase tracking-wide">
                        {menuCode.replace(/_/g, ' ')}
                      </h4>
                      <div className="space-y-2">
                        {menuPerms.map((perm) => {
                          const selectedActions = groupPermissions[perm.id] || [];
                          const allSelected = perm.actions_supported.every(a => selectedActions.includes(a));
                          
                          return (
                            <div key={perm.id} className="flex items-center gap-4 p-2 rounded hover:bg-slate-50">
                              <div className="w-48">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={allSelected}
                                    onCheckedChange={() => toggleAllActions(perm.id, perm.actions_supported)}
                                  />
                                  <span className="text-sm font-medium">
                                    {perm.permission_code.split('.').pop()}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 ml-6">{perm.description}</p>
                              </div>
                              
                              <div className="flex items-center gap-2 flex-1">
                                {ACTIONS.filter(a => perm.actions_supported.includes(a.code)).map((action) => {
                                  const isSelected = selectedActions.includes(action.code);
                                  return (
                                    <button
                                      key={action.code}
                                      onClick={() => togglePermissionAction(perm.id, action.code)}
                                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                        isSelected
                                          ? `bg-slate-800 text-white`
                                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                      }`}
                                    >
                                      <action.icon size={12} />
                                      {action.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-slate-500">
                <Shield size={48} className="mx-auto mb-4 opacity-30" />
                <p>Select a permission group to configure</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Group Dialog — same capsule pattern as the CEO/HOD Assign
          Task dialog: gradient violet header with a Shield icon +
          subtitle, uniform label rhythm, muted footer band. */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="group-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Shield size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">
                  {editingGroup ? 'Edit Permission Group' : 'Add Permission Group'}
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  {editingGroup
                    ? 'Update this group’s name, description or status. Group code is locked once created.'
                    : 'Bundle a set of permissions into a reusable group that roles can inherit.'}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Group Code
              </Label>
              <Input
                value={groupForm.group_code}
                onChange={(e) => setGroupForm({ ...groupForm, group_code: e.target.value })}
                placeholder="e.g., CRM_FULL_ACCESS"
                disabled={!!editingGroup}
                className="h-10 font-mono text-sm"
              />
              <p className="text-[11px] text-slate-400">
                A short uppercase identifier used in code — cannot be changed after creation.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Group Name
              </Label>
              <Input
                value={groupForm.group_name}
                onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                placeholder="CRM Full Access"
                className="h-10"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Description
              </Label>
              <Input
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="Full access to all CRM features"
                className="h-10"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Status
              </Label>
              <Select
                value={groupForm.status}
                onValueChange={(value) => setGroupForm({ ...groupForm, status: value })}
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveGroup}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              <Save size={14} className="mr-2" />
              {editingGroup ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionGroups;
